import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeftRight, Check, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-tap lodge transfer. Button opens a small dialog listing the lodges with
 * live availability for the booking's dates; one tap moves the guest — dates
 * and price stay exactly as they were. Shared by the bookings table and the
 * arrivals cards. Lodge bookings only (pitches are pooled).
 */
const MoveBookingDialog = ({ booking, onMoved, variant = "icon" }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [movingId, setMovingId] = useState(null);

  if (booking.unit_type !== "lodge") return null;
  if (["cancelled", "expired", "failed"].includes(booking.payment_status)) return null;

  const openDialog = async () => {
    setOpen(true);
    setOptions(null);
    try {
      const { data } = await axios.get(`${API}/admin/bookings/${booking.id}/move-options`);
      setOptions(data.options || []);
    } catch {
      toast.error("Couldn't load lodge availability.");
      setOpen(false);
    }
  };

  const move = async (opt) => {
    if (movingId || opt.current || !opt.available) return;
    setMovingId(opt.id);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${booking.id}/move-unit`, { unit_id: opt.id });
      toast.success(`Moved ${booking.guest_name || "guest"}: ${data.from} → ${data.to}. Price unchanged.`);
      setOpen(false);
      onMoved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't move the booking.");
    } finally {
      setMovingId(null);
    }
  };

  return (
    <>
      {variant === "icon" ? (
        <button
          type="button"
          onClick={openDialog}
          className="p-2 rounded-full text-[#6A5F58] hover:text-[#0F0E0E] hover:bg-[#F5EFE6] transition"
          title="Move to another lodge"
          data-testid={`booking-move-${booking.id}`}
        >
          <ArrowLeftRight size={15} />
        </button>
      ) : (
        <button
          type="button"
          onClick={openDialog}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6] transition text-[10px] font-brand tracking-[0.12em] uppercase"
          data-testid={`booking-move-${booking.id}`}
        >
          <ArrowLeftRight size={12} /> Move lodge
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4" data-testid="move-dialog">
          <div className="w-full max-w-sm bg-white rounded-2xl p-6 border border-[#DDD1BE]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-serif text-xl text-[#14100F]">Move {booking.guest_name || "guest"}</h3>
                <p className="text-xs text-[#6A5F58] mt-1">
                  {booking.start_date} → {booking.end_date} · price and dates stay as they are
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-[#6A5F58] hover:text-[#0F0E0E]" aria-label="Close" data-testid="move-dialog-close">
                <X size={16} />
              </button>
            </div>

            <div className="mt-4 space-y-2">
              {!options && (
                <p className="text-sm text-[#6A5F58] py-4 text-center">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Checking lodges…
                </p>
              )}
              {(options || []).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => move(opt)}
                  disabled={opt.current || !opt.available || movingId !== null}
                  className={`w-full flex items-center justify-between gap-2 rounded-xl border px-4 py-3 text-left transition ${
                    opt.current
                      ? "border-[#DDD1BE] bg-[#F5EFE6] opacity-60 cursor-default"
                      : opt.available
                        ? "border-[#DDD1BE] hover:border-[#B76E79] hover:bg-[#F5EFE6]/60"
                        : "border-[#EFE6D8] opacity-50 cursor-not-allowed"
                  }`}
                  data-testid={`move-option-${opt.id}`}
                >
                  <span>
                    <span className="block text-sm font-medium text-[#14100F]">{opt.name}</span>
                    <span className="block text-[11px] text-[#6A5F58] mt-0.5">
                      {opt.current ? "Current lodge" : opt.available ? `Free for these dates · £${Number(opt.nightly || 0).toFixed(0)}/night` : "Booked or blocked for these dates"}
                    </span>
                  </span>
                  {movingId === opt.id ? (
                    <Loader2 size={14} className="animate-spin text-[#B76E79] shrink-0" />
                  ) : opt.current ? (
                    <Check size={14} className="text-[#3B8C4E] shrink-0" />
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MoveBookingDialog;
